<script setup>

import BaseModal from '@/components/Alerts/BaseModal.vue'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import InputText from '@/components/Forms/InputText.vue'
import { computed, onMounted, ref } from 'vue'
import { FileStatus } from 'filepond'
import { useAuthStore } from '@/stores/authStore.js'
import vueFilePond from 'vue-filepond'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { Banner, Toaster } from '@/models/utils/Toaster.js';
import { ApiGatewayResponse, HttpStatusCode } from '@/api/ApiGateway.js'
import { Tenants } from '@/models/tenants/Tenants'
import { api } from '@/api/ApiGateway.js'
import { useFileDownload } from '@/composables/useFileDownload'
import { Util } from '@/models/utils/Util.js'
import ActionMenu from '@/components/Forms/ActionMenu.vue'
import UploadResultsModal from '@/views/Admin/User/UploadResultsModal.vue'
import { echoes } from '@/composables/echo/useEchoes.js'

defineProps({
	show: { type: Boolean, default: false },//Show modal
})

const emit = defineEmits(['evCloseModal'])//Used by ManageDocumentsPage to close modal

const authStore = useAuthStore()
const user = authStore.getAuth.user()
const toaster = new Toaster();
const banner = new Banner();

const showResultsModal = ref(false)
const uploadResults = ref({'successes': [], 'warnings': [], 'errors': []})
const uploadResultsTitle = ref('Upload Results')
const uploadResultsLoader = ref('')
const	validationErrors = ref(null)
const inviter_name_override = ref('')
const files = ref(null)
const tenant = ref(null)

//#region File Upload
const pond = ref(null)
const filesQueued = ref([])
const filesBeingProcessed = ref([])
const fileErrors = ref([])
const uploadProgress = ref(new Map())
const isProcessing = ref(false)
const { downloadFile } = useFileDownload()
const isTimeout = ref(false)

const FilePond = vueFilePond(FilePondPluginFileValidateType)
const serverConfig = {
	url: import.meta.env.VITE_API_URL + '/users/invites/upload',
	process: (fieldName, file, metadata, load, p_error, progress, abort) => {
		//unused params after abort: transfer, options
		const formData = new FormData()

		const upload_data = {
			"submitter_id": authStore.getAuth.user().id,
			"inviter_name_override": inviter_name_override.value,
			"resend_invites": false,
		}
		formData.append("upload_data", JSON.stringify(upload_data))

		formData.append(fieldName, file, file.name)
		const request = new XMLHttpRequest();
		request.open('POST', import.meta.env.VITE_API_URL + '/users/invites/upload')
		request.setRequestHeader('X-CSRF-TOKEN', window.csrf_token)
		request.setRequestHeader('Authorization', `Bearer ${useAuthStore().rawJwt}`)
		request.setRequestHeader('x-client-user-id', authStore.getAuth?.user()?.id)
		request.setRequestHeader('x-jwt-version-code', authStore.getAuth?.user()?.jwt_version_code)
		request.setRequestHeader('X-Socket-ID', echoes.socketId());

		//Not sure any of this timeout stuff is necessary
		request.timeout = 600000
		isTimeout.value = false
		request.ontimeout = function () {
			if (authStore.isDeveloper) console.log("request.ontimeout", arguments)
			isTimeout.value = true
			banner.info("Upload is being processed.")
			onFilesProcessed()
		}

		/* Capture 504 errors, the only way to fix this is to increase fastcgi_read_timeout in nginx.conf*/
		request.onerror = function () {
			if (authStore.isDeveloper) console.error("response.onerror!", request)

				banner.info("Upload is being processed.")
			onFilesProcessed()
		}

		request.upload.onprogress = (e) => {
			progress(e.lengthComputable, e.loaded, e.total)
		}

		request.onload = function () {
			console.log('request.onload', arguments)
			if (request.status >= 200 && request.status < 300) {
				load(request.responseText)
			}
			else
			{
				const resp = JSON.parse(request.response)
				if (resp.errors && (resp.errors.length > 0)) {
					validationErrors.value = new ApiGatewayResponse({
						'status': HttpStatusCode.ValidationError,
						'response': {
							'data': {
								'errors': resp.errors
							}
						}
					}, JSON.parse(request.response))
				}


				p_error('File upload failed.')
			}
		}

		request.send(formData)

		return {
			abort: () => {
				request.abort()
				abort()
			},
		};
	},
}

onMounted(() => {
	tenant.value = Tenants.getTenant()

	if (tenant.value.id === Tenants.AIAN)
	{
		inviter_name_override.value = "the American Indian Alaska Native Quality Improvement Organization"
	}
	else
	{
		inviter_name_override.value = user.given_name + " " + user.family_name
	}
})


const uploadEnabled = computed(() => {
	return (isProcessing.value === false && filesQueued.value != null && filesQueued.value.length > 0)
})

const inviter_text = computed(() => {
	if (inviter_name_override.value && inviter_name_override.value.length > 0) {
		return inviter_name_override.value
	}
	if (tenant.value.id === Tenants.AIAN)
	{
		return "the American Indian Alaska Native Quality Improvement Organization"
	}

	return user.given_name + " " + user.family_name
})

/**
 * Sets a document to submitted
 * @param {boolean} p_resend_expired_eh
 * @param p_on_success
 * @param onError
 */
function resendInvites(p_resend_expired_eh)
{
	if (p_resend_expired_eh)
	{
		uploadResults.value = {
			'successes': [],
			'warnings': [],
			'errors': []
		}
	}

	api.post('/users/invites/resend-invites', {"resend_invites": p_resend_expired_eh}).then(response =>
	{
		isProcessing.value = false


		if (authStore.isDeveloper) console.log('Resend invites response: ', response)

		if (response && response['error'] && response['error'] && response['error']['code'])
		{
			if (response['error']['code'] === "ERR_NETWORK")
			{
				banner.info('Due to emailing limits, processing will continue in background.')
				if (authStore.isDeveloper) console.log('Emails processing in background');
				return;
			}
		}

		if (response.hasError())
		{
			toaster.error('Error resending invites.')
			return
		}

		if (response?.response?.data && response?.response?.data['status'])
		{
			//Remove the created invites result since it's no longer needed.
			let index = uploadResults.value.successes.findIndex(p_item => p_item.heading === 'Invites Created For')
			if (index > -1)
			{
				uploadResults.value.successes.splice(index, 1)
			}

			handleUploadResponse(false, response.response.data.status)
		}
		else
		{
			if (authStore.isDeveloper) console.log('Upload response: ', response)
			toaster.warning("Unexpected response resending invites.")
		}
	})
}

function onFileAdded(p_error, file) {
	const outerContainer = document.getElementById('filepond--item-' + file.id)
	let infoMain = outerContainer.querySelector('.filepond--file-info-main')

	if (infoMain) {
		const wrapper = document.createElement('div')
		wrapper.classList.add('flex', 'space-x-2', 'filepond--file-info-main', 'w-full')
		infoMain.parentNode.insertBefore(wrapper, infoMain)
		infoMain.classList.remove('filepond--file-info-main')
		wrapper.appendChild(infoMain)

		const spinnerId = 'file-exists-spinner-' + file.id
		const warningId = 'file-exists-warning-' + file.id
		const fileExistsWrapper = document.createElement('div')
		fileExistsWrapper.classList.add('truncate')
		fileExistsWrapper.innerHTML = `
			<svg id="` + spinnerId + `" role="img" aria-labelledby="spinnerTitle" class="pointer-events-auto animate-spin h-4 w-4 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
				<title id="spinnerTitle">Checking for file name conflict...</title>
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
				<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
			</svg>
			<span id="` + warningId + `" class="hidden text-error pointer-events-auto" style="font-size: 14px;" title="A file with this name already exists. After uploading, please provide a description to avoid confusion.">A file with this name already exists. After uploading, please provide a description to avoid confusion.</span>
		`
		wrapper.appendChild(fileExistsWrapper)

		document.getElementById(spinnerId).classList.add("hidden")

	}
}

function onUpdateFiles(files) {
	filesBeingProcessed.value = []

	// Files about to begin processing
	filesBeingProcessed.value = []
	let filtered = files.filter((file) => {
		return file.status === FileStatus.PROCESSING_QUEUED
	})

	filtered.forEach((file) => {
		filesBeingProcessed.value.push(file)
	})

	// Files queued waiting for processing to start
	filesQueued.value = []
	filtered = files.filter((file) => {
		return file.status === FileStatus.IDLE
	})

	filtered.forEach((file) => {
		filesQueued.value.push(file)
	})

	isProcessing.value = filesBeingProcessed.value.length > 0
}

function onFileProcessed(p_error, p_file)
{
	if (authStore.isDeveloper) console.log("onFileProcessed", p_error, p_file)
	if (!p_file.serverId)
	{
		toaster.error('Error resending invites.')
	}
	const response_data = p_file.serverId ? JSON.parse(p_file.serverId) : null;
	if (response_data && response_data['status'])
	{
		handleUploadResponse(true, response_data.status, p_error);
	}
	else
	{
		banner.error('Error sending invites.')
	}


	if (p_error) fileErrors.value.push({ "error": p_error, "file": { "name": p_file.filename, "status": p_file.status } })

	if (isProcessingComplete())
	{
		onFilesProcessed()
	}
}

function handleUploadResponse(p_file_upload_eh, p_data)
{
	if (authStore.isDeveloper) console.log("handleUploadResponse", p_data)

	showUploadResults(p_file_upload_eh, p_data)
/*	return
	let banner_shown = false;

	let warn_msg = "", success_msg = "";
	if (p_file_upload_eh)
	{
		if (Util.objHasArray(p_data, 'created_invites'))
		{
			toaster.success("Created " + p_data['created_invites'].length + " new invites.")
		}
		else
		{
			toaster.info("No new invites were created.")
		}

		//Spreadsheet validation errors
		if (Util.objHasArray(p_data, 'excel_validation'))
		{
			if (warn_msg.length > 0) warn_msg += "<br/>"
			warn_msg += (Object.keys(p_data.excel_validation).length > 1) ? "These rows are " : "This row is ";
			warn_msg += "not valid:<br/> ";

			for (let i = 0; i < p_data['excel_validation'].length; i++)
			{
				const row = p_data['excel_validation'][i];
				warn_msg += row.label + ": " + row.text + "<br>";
			}
		}
	}
	else
	{
		//Org added to user
		if (Util.objHasArray(p_data, 'org_created'))
		{
			if (success_msg.length > 0) success_msg += "<br/>"
			success_msg += (p_data["org_created"].length > 1) ? "These users have " : "This user has ";
			success_msg += "an existing account, the organization has been added to their account: <br/>" + p_data["org_created"].join(", ") + "<br>";
		}

		//User already has org
		if (Util.objHasArray(p_data, 'existing_user_orgs'))
		{
			warn_msg += (p_data["existing_user_orgs"].length > 1) ? "These users have " : "This user has ";
			warn_msg += "an existing account with the requested organization: <br/>" + p_data["existing_user_orgs"].join(", ") + "<br>";
		}
	}


	// User already has an account with org
	if (warn_msg.length > 0)
	{
		banner.warning(warn_msg);
		banner_shown = true
	}

	// Invites sent to
	if (success_msg.length > 0)
	{
		banner.success(success_msg);
		banner_shown = true
	}

	let err_msg = "";
	if (Util.objHasArray(p_data, 'errors') || p_data['status'] === "error")
	{
		if (Util.objHasArray(p_data, 'error_inviting'))
		{
			const msg = (p_data["error_inviting"].length > 1) ? "these users: " : "this user: ";
			err_msg += "There was an error inviting " + msg + p_data["error_inviting"].join(", ") + "<br>";
		}

		if (Util.objHasArray(p_data, 'errors'))
		{
			err_msg += err_msg.length > 0 ? "<br/><br/>" : "";
			err_msg += "Errors: <br/>" + p_data["errors"].join("<br>") + "<br>";
		}
	}

	if (devEnvEh && Util.objHasArray(p_data, 'exceptions'))
	{
		err_msg += err_msg.length > 0 ? "<br/>" : "";
		err_msg += "Exceptions: <br/>" + p_data["exceptions"].join("<br/>");
	}

	if (err_msg.length > 0)
	{
		banner.error(err_msg);
		banner_shown = true
	}

	if (!banner_shown && !p_file_upload_eh)//Show basic toast if everything went well
	{
		toaster.success('Invite processing completed!')
	}*/

}

function showUploadResults(p_file_upload_eh, p_data)
{
	if (p_file_upload_eh)
	{
		uploadResultsTitle.value = "Invite File Upload Results"
		uploadResultsLoader.value = 'Emails sending'
	}
	else {
		uploadResultsTitle.value = "Invite Results"
		uploadResultsLoader.value = ''
	}

	if (Util.objHasArray(p_data, 'excel_validation'))
	{
		uploadResults.value.errors.push({'heading': 'Validation Errors', 'items':  p_data['excel_validation']})
	}

	if (Util.objHasArray(p_data, 'error_inviting'))
	{
		uploadResults.value.errors.push({'heading': 'Error Inviting These Emails', 'items':  p_data['error_inviting']})
	}

	if (Util.objHasArray(p_data, 'error_creating_user_org'))
	{
		uploadResults.value.errors.push({'heading': 'Error Creating User Organization for Existing User', 'items':  p_data['error_creating_user_org']})
	}

	if (Util.objHasArray(p_data, 'created_invites'))
	{
		uploadResults.value.successes.push({'heading': 'Invites Created For', 'items':  p_data['created_invites']})
	}

	if (Util.objHasArray(p_data, 'sent_invites'))
	{
		uploadResults.value.successes.push({'heading': 'Invites Sent for', 'items':  p_data['sent_invites']})
	}

	if (Util.objHasArray(p_data, 'org_created'))
	{
		uploadResults.value.successes.push({'heading': 'Organization Added to User', 'items':  p_data['org_created']})
	}

	if (Util.objHasArray(p_data, 'existing_user_orgs'))
	{
		uploadResults.value.warnings.push({'heading': 'User Exists and has Organization', 'items':  p_data['existing_user_orgs']})
	}

	if (authStore.isDeveloper && Util.objHasArray(p_data, 'errors'))
	{
		uploadResults.value.errors.push({'heading': 'Errors', 'items':  p_data['errors']})
	}

	if (authStore.isDeveloper && Util.objHasArray(p_data, 'exceptions'))
	{
		uploadResults.value.errors.push({'heading': 'Exceptions', 'items':  p_data['exceptions']})
	}

	if (uploadResults.value.successes.length > 0 || uploadResults.value.errors.length > 0 || uploadResults.value.warnings.length > 0)
	{
		pond.value.removeFiles();
		showResultsModal.value = true;
	}
	else if (Util.objHasArray(p_data, 'messages'))
	{
		banner.info(p_data['messages'].join(', '))
	}
}


/**
 * This is redundant in general, but in the case of a file that failed to upload retrying doesn't trigger onUpdateFiles()
 */
function onFileProcessStart(file)
{
	const fileAlreadyThere = filesBeingProcessed.value.find((item) => {
		return item.id === file.id
	})

	if (!fileAlreadyThere) {
		fileErrors.value = []
		filesBeingProcessed.value.push(file)
	}

}

/**
 * This was getting called once for every time there was a failed upload (like no doc name) which could cause
 * multiple Success toasts once successful. Removed from the file-pond component and called only when the file is successful.
 */
function onFilesProcessed()
{
	if (authStore.isDeveloper) console.log("onFilesProcessed - fileErrors:", fileErrors.value)
	isProcessing.value = false

	filesBeingProcessed.value = []
	uploadProgress.value = new Map()

	if (fileErrors.value.length === 0)
	{
		toaster.info("Starting invite processing...")
		resendInvites(false)

		filesQueued.value = []
		isProcessing.value = false
		//dismissModal(true)
	}
	else
	{
		toaster.error(errorDetails().join('. '))
	}
}

/**
 * Extracts the error details
 * @returns {string[]} Array of error strings.
 */
function errorDetails()
{
	const errors = []

	if (!validationErrors.value) return ['One or more files failed to upload.'];

	const rawErrors = validationErrors.value.error?.response?.data?.errors ?? []

	for (const i in rawErrors)
	{
		const error = rawErrors[i]
		if (Object.prototype.hasOwnProperty.call(error, 'detail'))
		{
			errors.push(error['detail'])
		}
	}
	if (errors.length <= 0)
	{
		errors.push('An unknown error was encountered.')
	}

	return errors
}

function onProcessFileProgress(file, progress) {
	uploadProgress.value.set(file.id, progress)
}

function onError()
{
	if (authStore.isDeveloper) console.log("onError", arguments)
	//unused params p_error, p_file, p_status
	isProcessing.value = false
}

/**
 * If one or more of the files have an error when uploading, the onFilesProcessed() function does not get called and isProcessing doesn't get updated to true
 */
function isProcessingComplete()
{
	if (authStore.isDeveloper) console.log("onFilesProcessed", arguments)
	const nonErrorStatuses = [FileStatus.IDLE, FileStatus.LOADING, FileStatus.PROCESSING_QUEUED, FileStatus.PROCESSING, FileStatus.PROCESSING_COMPLETE]

	//console.log('isProcessingComplete.filesBeingProcessed', filesBeingProcessed)
	const filesProcessingOrComplete = filesBeingProcessed.value.filter(file => {
		return file.status in nonErrorStatuses
	})

	return filesProcessingOrComplete.length === 0
}

function beginUpload()
{
	fileErrors.value = []
	isProcessing.value = true

	if (filesQueued.value != null && filesQueued.value.length > 0)
	{
		uploadResults.value = {
			'successes': [],
			'warnings': [],
			'errors': []
		}
		pond.value.processFiles()
	}
}

//#endregion File Upload

//#region Template Download


async function downloadTemplate()
{
	isProcessing.value = true
	toaster.info('Requesting download...', { timeout: false })

	await downloadFile(
		'/users/invites/download-template',
		null,
		'user-invite-template-' + Util.today('-', 'YYYY-MM-DD') + '.xlsx',
		onDownloadSuccess,
		onDownloadError)
}

function onDownloadSuccess(p_response)
{
	toaster.clear();
	isProcessing.value = false
	if (!p_response)
	{
		toaster.error('Template failed to download.')
	}
}

function onDownloadError(p_err, p_errs, p_auth_error)
{
	toaster.clear()
	isProcessing.value = false
	try
	{
		const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;
		toaster.clear()
		if (p_errs.length > 0)
		{
			const err_msgs = p_errs.map(p_item => p_item.detail);
			if (devEnvEh)
			{
				banner.error("Dev Only Banner:<br>" + err_msgs.join('<br/>'));
			}

			const toast_msg = p_auth_error ? "You do not have permission to download this template." : "Template failed to download.";
			toaster.error(toast_msg);
		}
		else
		{
			toaster.error('Template failed to download.')
		}
	}
	catch(e)
	{
		console.error(e)
	}
}
//#endregion Template Download

//#region Modal
/**
 * Check if there's any changes, if not, proceed with closing the modal
 * @param p_reload_eh True to reload the upload grid
 */
function dismissModal(p_reload_eh)
{
	closeModal(p_reload_eh)
}

/**
 * Do cleanup, show success toast and tell the UploadDocumentPage to close the modal
 * @param p_reload_eh
 */
function closeModal(p_reload_eh)
{
	cleanupModal()
	//if (p_reload_eh) toaster.success("User Invites Uploaded.")
	emit('evCloseModal', 'user_invite_upload', p_reload_eh)
}

/**
 * Tidy up
 */
function cleanupModal()
{
	validationErrors.value = null
	fileErrors.value = []
	filesQueued.value = []
	isProcessing.value = false
}
//#endregion Modal

//#region UI

function getMenuItems()
{
	const items = [];

	items.push({ id: 1, text: 'Download Template', action: 'template' });
	items.push({ id: 2, text: 'Resend Expired Invitations', action: 'retry' });

	return items
}

function onActionMenuItemSelected(p_menu_item)
{
	switch (p_menu_item.action)
	{
		case "template":
			downloadTemplate()
			break;
		case "retry":
			resendInvites(true);
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}
//#endregion UI

</script>

<template>
	<div>
		<BaseModal :show="show" border-classes="border-t-6 border-tertiary">
			<div class="w-136 h-192">
				<div class="flex justify-between items-center border-b border-neutral w-full pb-4">
					<p class="flex-grow text-xl font-bold text-heading">Upload Invites</p>
					<ActionMenu
						:items="getMenuItems()"
						@onActionMenuItemSelected="onActionMenuItemSelected"
						placement="bottom-end"
					></ActionMenu>
					<img src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="cursor-pointer size-4" @click.prevent="dismissModal(false)">
				</div>

				<form>
					<div class="flex flex-col mt-5">
						<div class="text-label">Email invite text</div>
						<div class="m-4">You've been invited to create a user account for the {{ tenant.name }} by <span class="userpilot-mask font-bold">{{ inviter_text }}</span>.</div>
						<InputText label="Invited by text" type="text" placeholder="" input-height="h-10" autocomplete="off"
						           v-model="inviter_name_override" :errors="validationErrors" error-field="inviter_name_override" icon="edit" />

						<label class="text-label mt-5 mb-2">Upload</label>
						<div class="border border-neutral rounded ">
							<!--
								allow-process: false removes retry button if fails
								labelTapToRetry, labelTapToCancel: set to blank cause disabling retry doesn't hide it for some reason  -->
							<file-pond
								class="h-30"
								name="file"
								ref="pond"
								v-bind:files="files"
								label-idle="Drop Excel file here or <span class='filepond--label-action'>Browse</span>"
								:allow-multiple="false"
								:max-files="1"
								:server="serverConfig"
								:allow-process="false"
								labelTapToRetry=""
								labelTapToCancel=""
								:allow-revert="false"
								:allow-remove="true"
								:instant-upload="false"
								:max-parallel-uploads="1"
								accepted-file-types="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"

								@addfile="onFileAdded"
								@processfilestart="onFileProcessStart"
								@processfile="onFileProcessed"
								@updatefiles="onUpdateFiles"
								@processfileprogress="onProcessFileProgress"
								@error="onError"
							/>
						</div>
					</div>
					<!-- Save/Cancel -->
					<div class="flex w-full flex-col-reverse sm:flex-row mt-4">
						<div class="sm:mr-auto"></div>
						<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" @click.prevent="dismissModal(false)"
						              class="sm:mr-3" />
						<SubmitButton label="Upload" class="" :size="ButtonSize.Small" @click.prevent="beginUpload" :disabled="!uploadEnabled" :loading="isProcessing" />
					</div>

				</form>
			</div>
		</BaseModal>
	</div>

	<Teleport to="html">
		<UploadResultsModal :show="showResultsModal" :results="uploadResults" :title="uploadResultsTitle" :loader="uploadResultsLoader" @evCloseModal="showResultsModal = false" />
	</Teleport>
</template>

<style scoped>
/* deep needed for these styles to work with 'scoped' */
:deep(.filepond--panel)
{
	display:none !important;
}

:deep(.filepond--root .filepond--drop-label) {
	width: 100%;
	height: fit-content !important;
	transform: translate3d(0, 0, 0) !important;
	color: #0a0a0a;
	text-align: center;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 1.5;
	padding: 3em 0em !important;

	label {
		width: 100% !important;
	}
}

:deep(.filepond--list) {
	.filepond--item {
		background-color: #EFF6FB !important;
		@apply border border-disabled rounded-md;
	}

	.filepond--file, .filepond--file-info-sub, .filepond--file-status-main, .filepond--file-status-sub {
		color: #11181D !important;
		@apply font-normal !important;
		opacity: 1;
	}

	.filepond--file-action-button:hover, .filepond--file-action-button:focus {
		@apply !ring-2 !ring-primary-darkest/65;
	}
}

</style>